// Camera assignment for a PC with more than one webcam (Manage Rule →
// Camera Settings). Two roles:
//
//   'pointWatching' — the hidden always-on stream in Base that snaps a photo
//                     on every enrollment (shown in Point Watching).
//   'general'       — every screen that opens a camera for the employee:
//                     customer form/profile, raffle, bonus, ticket out, ...
//
// The rule stores both the browser deviceId and the camera label. The
// deviceId is exact but changes when the browser's site data is cleared; the
// label survives that. A camera that isn't plugged into THIS device (a phone,
// another PC) simply doesn't match, and the caller falls back to whatever it
// opened before this setting existed.
import AuthUtil from '../../redux/AuthUtil';
import { RULES } from '../global/Server/endpoints';

export const CAMERA_ROLES = {
  POINT_WATCHING: 'pointWatching',
  GENERAL: 'general',
};

const EMPTY = {
  pointWatchingCameraId: '',
  pointWatchingCameraLabel: '',
  generalCameraId: '',
  generalCameraLabel: '',
};

// Fired on window after Manage Rule saves, so Base can swap its stream.
export const CAMERA_SETTINGS_CHANGED_EVENT = 'app:camera-settings-changed';

// locationId -> { promise, at }. Re-read after CACHE_TTL_MS so a kiosk tab
// that stays open for days still picks up a change made from another
// computer.
const cache = {};
const CACHE_TTL_MS = 5 * 60 * 1000;

// Read lazily: importing the store at module load would cycle through the
// reducers/sagas that already import components.
const currentLocationId = () => {
  try {
    // eslint-disable-next-line global-require
    const { store } = require('../../redux/store');
    const state = store.getState();
    return ((state && state.auth && state.auth.userInfo) || {})
      .selectedLocationId;
  } catch (err) {
    return null;
  }
};

export const fetchCameraSettings = (locationId, force = false) => {
  const loc = locationId || currentLocationId();
  if (!loc) return Promise.resolve({ ...EMPTY });
  const hit = cache[loc];
  if (!force && hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.promise;
  const entry = { at: Date.now() };
  entry.promise = AuthUtil.postRequest(RULES.CAMERA_SETTINGS, {
    locationId: loc,
  })
    .then((res) => ({ ...EMPTY, ...((res && res.data) || {}) }))
    .catch(() => {
      // Never block a camera over a settings fetch (e.g. the PC just booted
      // and the network isn't up yet); retry on the next open.
      if (cache[loc] === entry) delete cache[loc];
      return { ...EMPTY };
    });
  cache[loc] = entry;
  return entry.promise;
};

// Manage Rule calls this after a save so this tab picks up the change
// without a reload.
export const primeCameraSettings = (locationId, settings) => {
  if (!locationId) return;
  cache[locationId] = {
    at: Date.now(),
    promise: Promise.resolve({ ...EMPTY, ...(settings || {}) }),
  };
};

export const listCameras = async () => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
    return [];
  }
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((d) => d.kind === 'videoinput');
  } catch (err) {
    return [];
  }
};

/**
 * deviceId to open for `role` on this device, or null to keep the caller's
 * own default. Matches on deviceId first, then on label.
 */
export const resolveCameraDeviceId = async (role, locationId) => {
  const settings = await fetchCameraSettings(locationId);
  const savedId = settings[`${role}CameraId`];
  const savedLabel = settings[`${role}CameraLabel`];
  if (!savedId && !savedLabel) return null;

  const cams = await listCameras();
  if (savedId && cams.some((c) => c.deviceId === savedId)) return savedId;
  if (savedLabel) {
    const byLabel = cams.find((c) => c.label === savedLabel);
    if (byLabel && byLabel.deviceId) return byLabel.deviceId;
  }
  // Before camera permission is granted Chrome hides ids and labels, so
  // nothing can match yet. Try the saved id; the caller falls back if it
  // isn't on this device.
  const hidden = cams.length > 0 && cams.every((c) => !c.deviceId);
  return hidden && savedId ? savedId : null;
};

/**
 * getUserMedia on the camera assigned to `role`, falling back to
 * `fallbackConstraints` (the screen's pre-existing constraints) when none is
 * assigned or the assigned one can't be opened here.
 */
export const openRoleCamera = async (role, fallbackConstraints, locationId) => {
  const deviceId = await resolveCameraDeviceId(role, locationId);
  if (deviceId) {
    try {
      // Keep the screen's resolution/frame-rate hints; facingMode is dropped
      // because the pinned device already decides which camera it is.
      const hints =
        fallbackConstraints && typeof fallbackConstraints.video === 'object'
          ? { ...fallbackConstraints.video }
          : {};
      delete hints.facingMode;
      return await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { ...hints, deviceId: { exact: deviceId } },
      });
    } catch (err) {
      const name = err && err.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') throw err;
    }
  }
  return navigator.mediaDevices.getUserMedia(fallbackConstraints);
};

/** deviceId a live stream is actually using ('' if unknown). */
export const streamDeviceId = (stream) => {
  const track = stream && stream.getVideoTracks && stream.getVideoTracks()[0];
  return (track && track.getSettings && track.getSettings().deviceId) || '';
};
